import { Box, Flex, FlexProps, UseDisclosureReturn } from "@chakra-ui/react"
import { useSize } from "@pathwright/ux/src/utils/size"
import { AnimatePresence, motion } from "framer-motion"
import React from "react"
import { usePreviousDistinct } from "react-use"
import { exhaustiveGuard } from "../../utils/typing"
import AugmentationPositionedToolbar from "./AugmentationPositionedToolbar"
import AugmentationToolbar, {
  AugmentationToolbarHookReturn,
  useAugmentationDisclosedToolbar
} from "./AugmentationToolbar"
import { UseAugmentationPanelProps, usePanelSize } from "./useAugmentationPanel"

export type AugmentationPanel = ReturnType<
  typeof React.forwardRef<
    HTMLElement,
    UseAugmentationPanelProps & {
      toolbar: AugmentationToolbarHookReturn
      disclosure: UseDisclosureReturn
    }
  >
>

// The AugmentationComponents is an object that is keyed with  "Primary", "Panel",
// and optionally "Toolbar" to allow for specifying the order of the components
// (since an object's keys will retain order when doing Object.keys()).
// This can (potentially) be used to determine the order of the components
// (like whether the panel is to the left or right of the primary view).
export type AugmentationComponents = {
  Primary?: React.FC<any>
  Panel: AugmentationPanel
  Toolbar?: typeof AugmentationToolbar | typeof AugmentationPositionedToolbar
}

export type AugmentationLayoutProps = {
  components: AugmentationComponents
  toolbar: AugmentationToolbarHookReturn
  disclosure: UseDisclosureReturn
  // Spacing between layout elements.
  spacing?: number | [number, number] | [number, number, number]
  // Allow the augmentation panel to completely overlay the primary view at smaller screen sizes.
  allowOverlay?: boolean
  // Position the Panel relative to the Primary view.
  panelPosition?: "near" | "far"
}

const AugmentationLayout = ({
  components,
  toolbar,
  disclosure,
  allowOverlay,
  spacing,
  panelPosition = "far"
}: AugmentationLayoutProps) => {
  const [layoutRef, layoutWidth] = useSize("width")
  const [primaryRef, primaryWidth] = useSize("width")
  let [panelRef, panelWidth] = useSize("width")
  const [toolbarRef, toolbarWidth] = useSize("width")

  // We need to preserve the previous panel size to fall back to since
  // otherwise the repoening the panel can result in animating the panel
  // initially from an incorrect position.
  const prevPanelWidth = usePreviousDistinct(panelWidth)

  const { Primary, Panel, Toolbar } = components
  const disclosedToolbar = useAugmentationDisclosedToolbar(toolbar, disclosure)

  // Default the spacing when not provided.
  const spacings = ([] as number[]).concat(spacing || 10)
  const baseSpacing = spacings[0]

  const primaryAndPanelSpacing = spacings[0] || baseSpacing
  const panelAndToolbarSpacing =
    disclosure.isOpen && Toolbar ? spacings[1] || baseSpacing : 0
  const layoutSpacing = spacings[2] || baseSpacing

  // TODO: work out how to adjust spacing based on number of components,
  // order of components, and number of spacings.
  // let primaryAndPanelSpacing, panelAndToolbarSpacing, layoutSpacing
  // if (spacings.length === 1) {
  //   primaryAndPanelSpacing = baseSpacing
  //   panelAndToolbarSpacing = baseSpacing
  //   layoutSpacing = baseSpacing
  // } else if (spacings.length === 2) {
  //   primaryAndPanelSpacing = spacings[0]
  //   panelAndToolbarSpacing = spacings[1]
  //   layoutSpacing = spacings[1]
  // } else {
  //   primaryAndPanelSpacing = spacings[0]
  //   panelAndToolbarSpacing = spacings[1]
  //   layoutSpacing = spacings[2]
  // }

  // Choose to align the augmentation (panel + toolbar) to the left or right.
  // This will be used to determine the offset of the primary view.
  // NOTE: aligning panel to the left does not currently work. This requires
  // updating teh useAugmentationPanel hook to support left alignment.
  // Also, toolbar doesn't respect the alignment either.
  const alignAugmentation =
    Object.keys(components).indexOf("Panel") >
    Object.keys(components).indexOf("Primary")
      ? "right"
      : "left"

  const panelTokenSize = usePanelSize({ allowOverlay })

  // Widths of the elements.
  panelWidth ||= prevPanelWidth || 0

  // Seems to be a bug in useMeasure that retains the size of the element even after it
  // has unmounted. So, we need to manually track whether the panel is open, and, if not,
  // reset panel width to 0.
  if (!disclosure.isOpen) {
    panelWidth = 0
  }

  // Ignoring offset when panel is full size.
  if (panelTokenSize === "full") {
    panelWidth = 0
  }

  // The panel + toolbar width.
  const panelPlusToolbar = panelWidth + panelAndToolbarSpacing + toolbarWidth
  // The primary + panel + toolbar width.
  const primaryPlusPanelPlusToolbar =
    primaryWidth + primaryAndPanelSpacing + panelPlusToolbar

  // Offsetting the panel from the side of the layout.
  // This currently assumes the toolbar is positioned adjacent to the panel.
  let panelOffset = panelAndToolbarSpacing + toolbarWidth
  // Default to offset the toolbar from the side of the layout by the layout spacing.
  let toolbarOffset = layoutSpacing
  // Offsetting the primary from the same side of the layout.
  // This is to make room for displaying the panel + toolbar adjacent to the primary.
  // when the layout contains all elements (no overflowing elements).
  let primaryOffset = 0

  // We can use the centers of the layour and primary to derive the position
  // of the left and right edges of the primary. This approach is necessary,
  // rather than getting the primarySize left and right values, because
  // these values aren't updated by the resize observer (but widths are).
  // The center of the layout.
  const layoutCenter = layoutWidth / 2
  // The center of the primary.
  const primaryCenter = primaryWidth / 2

  // Does the layout contain all elements within its width?
  // If so, then we can offset the priamary if necessary.
  // If not, then the primary will be "auto" offset from the [side] of layout.
  const layoutContainsAll = layoutWidth > primaryPlusPanelPlusToolbar

  // Only ready to account for offests when all elements are present
  // and widths are known.
  const isReady = Boolean(
    primaryWidth && (!Toolbar || toolbarWidth) && layoutWidth
  )

  // We can simplify offset logic by adjusting the position of the primary
  // to the right (or left, when implemented), then apply the offset from
  // that side to account for the panel + toolbar. This is simpler than
  // always adjusting the offset for a centered primary view, which would
  // constantly need to adjust as the augmentation layout width changed.
  let primaryHorizontalAlignment: FlexProps["justify"] = "center"
  let primaryOffsetValue = "0px"

  // When ready, adjust the default alignment and offset of the primary view.
  if (isReady) {
    // When the layout contains all elements, we can offset the primary.

    // If panel overlaps primary:
    //  - Align the primary view to the [side] of layout.
    //  - Offset the primary view from that [side] by the width of the panel + toolbar.
    // If the primary overlaps the layout:
    //  - Align the primary view to the [side] of layout.
    //  - Offset the primary view from that [side] by the remaining layout width ("auto" margin).
    // Otherwise:
    //  - Align the primary view to the center of the layout.
    //  - Do not offset the primary view.
    if (layoutContainsAll) {
      // Width of the augmentation (panel + toolbar) when aligned to the side.
      const augmentationWidth = primaryAndPanelSpacing + panelPlusToolbar
      // We can subtract the centered primary edge from the layout width
      // to determine how much space is available for the augmentation.
      const centeredPrimaryEdge = layoutCenter + primaryCenter
      const availableLayoutWidth = layoutWidth - centeredPrimaryEdge
      // If there's not enought space for the augmentation, we need to offset
      // the primary view to allow for the augmentation to fit.
      primaryOffset =
        availableLayoutWidth < augmentationWidth ? augmentationWidth : 0

      // When the panel is positioned "near" the primary view, we need to
      // offset the panel and toolbar be adjacent to the [side] of the primary view.

      // TODO: simpplify and fix this logic. Mostly spacing everything correctly, but it
      // would seem we could improve.
      if (panelPosition === "near") {
        // When the primary is being offset, we can adjust all offsets so that all
        // elements are centered in the layout.
        if (primaryOffset) {
          const remainingLayoutWidth = layoutWidth - primaryPlusPanelPlusToolbar
          const nearOffset = remainingLayoutWidth / 2
          panelOffset += nearOffset
          toolbarOffset += nearOffset
          primaryOffset += nearOffset
        } else {
          // When there is no primary offset, we shift the panel and toolbar
          // to the side of the primary view based on the primary's edge.
          const remainingLayoutWidth = layoutWidth - centeredPrimaryEdge
          // Accounting for gutter spacing on the panel (- layoutSpacing).
          // The gutter spacing pushes the panel inward from the edge of the layout.
          panelOffset =
            remainingLayoutWidth - primaryAndPanelSpacing - panelWidth
          toolbarOffset = panelOffset - panelAndToolbarSpacing - toolbarWidth
        }

        // Accounting for gutter spacing on the panel (- layoutSpacing).
        // The gutter spacing pushes the panel inward from the edge of the layout.
        panelOffset -= layoutSpacing
      }

      if (primaryOffset) {
        // When offsetting the primary, we align the primary to the side of the
        // panel + toolbar, and offset the primary from that side (using margin[Left|Right]).
        primaryHorizontalAlignment =
          alignAugmentation === "left" ? "flex-start" : "flex-end"

        primaryOffsetValue = `${primaryOffset}px`
      }
    } else {
      // When the layout doesn't contain all elements, we need simply offset
      // the primary as far from the side of the layout as possible to allow
      // as much of the primary to be visible behind the partially overlaying
      // panel + toolbar.
      primaryOffsetValue = "auto"
    }
  }

  // Depending on alignment of panel, offset the primary from that side.
  const primaryOffsetMarginKey =
    alignAugmentation === "left" ? "marginLeft" : "marginRight"

  // The margin[Left|Right] offset of the primary view.
  const primaryOffsetProp = {
    [primaryOffsetMarginKey]: primaryOffsetValue
  }

  // NOTE: the "sizer" elements are used to determine the width of their parent.
  // They explicitly are rendered at the top of the parent element with 0 height
  // so that the resize observer doesn't concern itself with the height of the
  // parent element.
  return (
    <Box className="AugmentationLayout" w="100%">
      {/* Layout width sizer */}
      <Box ref={layoutRef} h={0} w="100%" />
      <Flex
        justify={primaryHorizontalAlignment}
        align="flex-start"
        maxW="100%"
        pos="relative"
      >
        {(Object.keys(components) as (keyof AugmentationComponents)[]).map(
          (key) => {
            switch (key) {
              case "Primary":
                return Primary ? (
                  <AnimatePresence key={key} initial={false}>
                    <Box
                      as={motion.div}
                      layout="position"
                      {...primaryOffsetProp}
                      // {...primaryPaddingMarginProp}
                    >
                      {/* Primary width sizer */}
                      <Box ref={primaryRef} h={0} w="100%" />
                      {/* TODO: remove this dependency OR type the Primary props as such. */}
                      <Primary {...disclosedToolbar} />
                    </Box>
                  </AnimatePresence>
                ) : null
              case "Panel":
                return (
                  <Panel
                    key={key}
                    ref={panelRef}
                    toolbar={toolbar}
                    disclosure={disclosure}
                    allowOverlay={allowOverlay}
                    gutter={layoutSpacing}
                    offset={{ [alignAugmentation]: panelOffset }}
                  />
                )
              case "Toolbar":
                return Toolbar ? (
                  <Toolbar
                    key={key}
                    ref={toolbarRef}
                    allowOverlay={allowOverlay}
                    gutter={layoutSpacing}
                    offset={{ [alignAugmentation]: toolbarOffset }}
                    toolbar={disclosedToolbar}
                  />
                ) : null
              default:
                exhaustiveGuard(key)
            }
          }
        )}
      </Flex>
    </Box>
  )
}

export default AugmentationLayout
